import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  StickyNote, Plus, Search, Pencil, Trash2, Camera, Upload, X,
  FileText, Loader2, Archive, ArchiveRestore, GitBranch,
  Image as ImageIcon, Inbox, Calculator, SpellCheck2, Ruler,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import CalculatorDialog from "@/components/CalculatorDialog";
import MaterialEstimateDialog from "@/components/MaterialEstimateDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const STATUS_META = {
  aberta:     { label: "Aberta",     bg: "bg-violet-100",  text: "text-[#7c3aed]",     dot: "bg-violet-500" },
  arquivada:  { label: "Arquivada",  bg: "bg-slate-200",   text: "text-slate-600",     dot: "bg-slate-400"  },
  convertida: { label: "Convertida", bg: "bg-emerald-100", text: "text-emerald-700",   dot: "bg-emerald-500" },
};

export default function Anotacoes() {
  const nav = useNavigate();
  const [notes, setNotes] = useState([]);
  const [schools, setSchools] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("aberta");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, note: null });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmConvert, setConfirmConvert] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/notes"),
        api.get("/schools"),
      ]);
      setNotes(r1.data || []);
      setSchools(r2.data || []);
    } catch {
      toast.error("Erro ao carregar anotações");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return notes.filter((n) => {
      if (statusFilter !== "all" && n.status !== statusFilter) return false;
      if (s && !((n.title || "") + " " + (n.content || "") + " " + (n.school_name || "")).toLowerCase().includes(s)) return false;
      return true;
    });
  }, [notes, search, statusFilter]);

  const counts = useMemo(() => ({
    all: notes.length,
    aberta: notes.filter((n) => n.status === "aberta").length,
    arquivada: notes.filter((n) => n.status === "arquivada").length,
    convertida: notes.filter((n) => n.status === "convertida").length,
  }), [notes]);

  const openNew = () => setDialog({ open: true, note: null });
  const openEdit = (n) => setDialog({ open: true, note: n });

  const remove = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/notes/${id}`);
      toast.success("Anotação removida");
      setNotes((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível remover");
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  const convert = async (n) => {
    setBusyId(n.id);
    try {
      const r = await api.post(`/notes/${n.id}/convert`);
      toast.success(`Orçamento Nº ${r.data.number} criado!`);
      nav(`/orcamentos/${r.data.budget_id}/editar`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao converter em orçamento");
    } finally {
      setBusyId(null);
      setConfirmConvert(null);
    }
  };

  const toggleArchive = async (n) => {
    const nextStatus = n.status === "arquivada" ? "aberta" : "arquivada";
    setBusyId(n.id);
    // Optimistic update
    setNotes((arr) => arr.map((x) => (x.id === n.id ? { ...x, status: nextStatus } : x)));
    try {
      await api.put(`/notes/${n.id}`, { status: nextStatus });
      toast.success(nextStatus === "arquivada" ? "Anotação arquivada" : "Anotação reaberta");
    } catch (e) {
      // Rollback
      setNotes((arr) => arr.map((x) => (x.id === n.id ? { ...x, status: n.status } : x)));
      toast.error(e?.response?.data?.detail || "Não foi possível arquivar");
    } finally {
      setBusyId(null);
    }
  };

  const NewButton = (
    <Button
      onClick={openNew}
      className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white shadow-sm"
      data-testid="btn-new-note"
    >
      <Plus className="w-4 h-4 mr-1.5" /> Nova anotação
    </Button>
  );

  return (
    <div className="space-y-5 pb-28">
      <PageHeader
        icon={StickyNote}
        title="Anotações"
        subtitle="Lembretes rápidos por escola — vire orçamento quando quiser"
        right={
          <>
            {/* Desktop: botão largo com rótulo */}
            <div className="hidden sm:block">{NewButton}</div>
            {/* Mobile: botão compacto "+" sempre visível ao lado do título */}
            <button
              type="button"
              onClick={openNew}
              className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#7c3aed] hover:bg-[#6d28d9] text-white shadow-sm active:scale-95 transition-transform"
              data-testid="btn-new-note-mobile"
              aria-label="Nova anotação"
            >
              <Plus className="w-5 h-5" strokeWidth={2.6} />
            </button>
          </>
        }
      />

      {/* Status tabs with counts (mobile-friendly) */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 no-scrollbar">
        {[
          { k: "aberta",     label: "Abertas",     Icon: StickyNote },
          { k: "arquivada",  label: "Arquivadas",  Icon: Archive },
          { k: "convertida", label: "Convertidas", Icon: GitBranch },
          { k: "all",        label: "Todas",       Icon: Inbox },
        ].map(({ k, label, Icon }) => {
          const active = statusFilter === k;
          return (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              data-testid={`tab-${k}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-full border text-xs font-semibold transition-colors ${
                active
                  ? "bg-[#7c3aed] text-white border-[#7c3aed] shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              <span className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold ${
                active ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
              }`}>
                {counts[k] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por título, escola ou conteúdo..."
          className="pl-9"
          data-testid="input-search-notes"
        />
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Carregando anotações...
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          hasAny={notes.length > 0}
          onNew={openNew}
          searching={!!search.trim()}
          filter={statusFilter}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((n) => {
            const meta = STATUS_META[n.status] || STATUS_META.aberta;
            const isBusy = busyId === n.id;
            return (
              <div
                key={n.id}
                data-testid={`note-card-${n.id}`}
                className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 flex flex-col relative"
              >
                {isBusy && (
                  <div className="absolute inset-0 bg-white/70 rounded-2xl flex items-center justify-center z-10">
                    <Loader2 className="w-5 h-5 animate-spin text-[#7c3aed]" />
                  </div>
                )}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-heading font-bold text-[#1e3a8a] text-sm leading-tight line-clamp-2 flex-1 min-w-0">
                    {n.title}
                  </h3>
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider rounded-full px-2 py-0.5 ${meta.bg} ${meta.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} /> {meta.label}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mb-2 truncate">
                  {n.school_name || "—"} · {formatDateBR(n.created_at)}
                </p>
                {n.content && (
                  <p className="text-xs text-slate-700 line-clamp-3 whitespace-pre-wrap">{n.content}</p>
                )}
                {n.photos?.length > 0 && (
                  <div className="mt-2 grid grid-cols-4 gap-1">
                    {n.photos.slice(0, 4).map((p, i) => (
                      <div key={i} className="aspect-square rounded border border-slate-200 overflow-hidden">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                    {n.photos.length > 4 && (
                      <div className="aspect-square rounded border border-slate-200 bg-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-600">
                        +{n.photos.length - 4}
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-auto pt-3 mt-3 flex flex-wrap gap-1.5 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(n)}
                    className="h-8 text-[11px]"
                    data-testid={`btn-edit-note-${n.id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Editar
                  </Button>
                  {n.status !== "convertida" && (
                    <Button
                      size="sm"
                      onClick={() => setConfirmConvert(n)}
                      className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`btn-convert-note-${n.id}`}
                    >
                      <GitBranch className="w-3 h-3 mr-1" /> Virar orçamento
                    </Button>
                  )}
                  {n.converted_budget_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => nav(`/orcamentos/${n.converted_budget_id}`)}
                      className="h-8 text-[11px]"
                      data-testid={`btn-view-budget-${n.id}`}
                    >
                      <FileText className="w-3 h-3 mr-1" /> Ver orç.
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleArchive(n)}
                    className="h-8 text-[11px]"
                    data-testid={`btn-archive-note-${n.id}`}
                  >
                    {n.status === "arquivada" ? (
                      <><ArchiveRestore className="w-3 h-3 mr-1" /> Reabrir</>
                    ) : (
                      <><Archive className="w-3 h-3 mr-1" /> Arquivar</>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmDelete(n)}
                    className="h-8 text-[11px] text-rose-600 hover:bg-rose-50"
                    data-testid={`btn-delete-note-${n.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile floating action button — always available */}
      <button
        onClick={openNew}
        data-testid="fab-new-note"
        className="sm:hidden fixed bottom-24 right-5 w-14 h-14 rounded-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white shadow-lg shadow-violet-300/50 flex items-center justify-center z-40 active:scale-95 transition-transform"
        aria-label="Nova anotação"
      >
        <Plus className="w-6 h-6" strokeWidth={2.5} />
      </button>

      <NoteDialog
        open={dialog.open}
        note={dialog.note}
        schools={schools}
        onClose={() => setDialog({ open: false, note: null })}
        onSaved={() => { setDialog({ open: false, note: null }); load(); }}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover anotação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A anotação <strong>{confirmDelete?.title}</strong> será excluída.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && remove(confirmDelete.id)}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-note"
            >
              Sim, remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert confirm */}
      <AlertDialog open={!!confirmConvert} onOpenChange={(o) => !o && setConfirmConvert(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Converter em orçamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Vamos criar um orçamento em branco para <strong>{confirmConvert?.school_name}</strong> usando o conteúdo da anotação como ponto de partida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmConvert && convert(confirmConvert)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-convert-note"
            >
              Sim, converter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------- Empty State ---------- */
function EmptyState({ hasAny, onNew, searching, filter }) {
  if (searching) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
        <Search className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm">Nenhuma anotação bate com a busca.</p>
      </div>
    );
  }
  const copy = {
    aberta:     { title: "Sem anotações abertas",     sub: "Crie um lembrete rápido pra não esquecer de nenhum serviço solicitado." },
    arquivada:  { title: "Sem arquivos",              sub: "Anotações arquivadas aparecem aqui depois que você guardar as resolvidas." },
    convertida: { title: "Nenhuma convertida ainda",  sub: "Quando você virar uma anotação em orçamento, ela aparece aqui pra consulta." },
    all:        { title: "Nenhuma anotação ainda",    sub: "Comece registrando os pedidos informais que recebe por WhatsApp, telefone ou pessoalmente." },
  }[filter] || { title: "Sem anotações", sub: "" };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
      <div className="w-14 h-14 rounded-2xl bg-violet-100 text-[#7c3aed] mx-auto mb-3 flex items-center justify-center">
        <StickyNote className="w-7 h-7" />
      </div>
      <p className="text-base font-bold text-[#1e3a8a] mb-1">{copy.title}</p>
      <p className="text-slate-500 text-sm max-w-xs mx-auto">{copy.sub}</p>
      {(filter === "aberta" || filter === "all" || !hasAny) && (
        <Button
          onClick={onNew}
          className="mt-4 bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
          data-testid="btn-empty-new-note"
        >
          <Plus className="w-4 h-4 mr-1.5" /> {hasAny ? "Criar nova anotação" : "Criar primeira anotação"}
        </Button>
      )}
    </div>
  );
}

/* ---------- Dialog ---------- */
function NoteDialog({ open, note, schools, onClose, onSaved }) {
  const [schoolId, setSchoolId] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState("");
  // Ajudantes IA/utilitários (mesmos do formulário de orçamento, exceto a
  // IA de "criar item" — aqui o foco é redação/estimativa/cálculo auxiliar).
  const [calcOpen, setCalcOpen] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [spellLoading, setSpellLoading] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const spellCorrectNote = async () => {
    const prompt = content?.trim();
    if (!prompt) { toast.error("Digite algo primeiro"); return; }
    setSpellLoading(true);
    try {
      const r = await api.post("/ai/spell-correct", { prompt });
      setContent(r.data.text || content);
      toast.success("Português corrigido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao corrigir");
    } finally {
      setSpellLoading(false);
    }
  };

  const appendEstimate = (chunk) => {
    if (!chunk) return;
    setContent((prev) => (prev ? `${prev.trimEnd()}\n\n${chunk}` : chunk));
    setEstimateOpen(false);
    toast.success("Estimativa adicionada à anotação");
  };

  useEffect(() => {
    if (open) {
      setSchoolId(note?.school_id || "");
      setTitle(note?.title || "");
      setContent(note?.content || "");
      setPhotos(note?.photos || []);
      setSchoolSearch("");
    }
  }, [open, note]);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const added = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (r.data?.path) added.push(r.data.path);
      }
      if (added.length) {
        setPhotos((p) => [...p, ...added]);
        toast.success(`${added.length} foto${added.length > 1 ? "s" : ""} adicionada${added.length > 1 ? "s" : ""}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar fotos");
    } finally {
      setUploading(false);
      if (galleryRef.current) galleryRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const removePhoto = (p) => setPhotos((arr) => arr.filter((x) => x !== p));

  const sortedSchools = useMemo(() => {
    const s = schoolSearch.trim().toLowerCase();
    const list = [...schools].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return s ? list.filter((x) => (x.name || "").toLowerCase().includes(s)) : list;
  }, [schools, schoolSearch]);

  const save = async () => {
    if (!schoolId) { toast.error("Selecione uma escola"); return; }
    if (!title.trim()) { toast.error("Dê um título à anotação"); return; }
    setSaving(true);
    try {
      if (note?.id) {
        await api.put(`/notes/${note.id}`, { title: title.trim(), content, photos });
        toast.success("Anotação atualizada");
      } else {
        await api.post("/notes", { school_id: schoolId, title: title.trim(), content, photos });
        toast.success("Anotação criada");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold text-[#1e3a8a]">
            {note?.id ? "Editar anotação" : "Nova anotação"}
          </DialogTitle>
          <DialogDescription>
            {note?.id ? "Atualize os detalhes abaixo." : "Registre um pedido informal — depois você vira em orçamento."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Escola *</label>
            {note?.id ? (
              <div className="h-10 px-3 flex items-center text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-700">
                {note.school_name}
              </div>
            ) : (
              <Select value={schoolId} onValueChange={setSchoolId}>
                <SelectTrigger data-testid="select-note-school"><SelectValue placeholder="Selecione uma escola..." /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <Input
                      value={schoolSearch}
                      onChange={(e) => setSchoolSearch(e.target.value)}
                      placeholder="Buscar escola..."
                      className="h-8 text-xs"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  {sortedSchools.length === 0 ? (
                    <div className="p-3 text-xs text-slate-500 text-center">Nenhuma escola encontrada</div>
                  ) : sortedSchools.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Título *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Reforma banheiro bloco B"
              data-testid="input-note-title"
              autoFocus={!note?.id}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Descrição</label>
            <Textarea
              rows={5}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Detalhes, medidas, materiais, prazos, nome do solicitante..."
              data-testid="input-note-content"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-slate-400">{content.length} caracteres</p>
            </div>
            {/* Toolbar de ajudantes — mesmos do formulário de orçamento,
                exceto o IA que CRIA itens. Aqui redação, estimativa e cálculo. */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={spellCorrectNote}
                disabled={spellLoading || !content.trim()}
                className="h-8 text-[11px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="btn-note-spell-correct"
                title="Corrigir ortografia / pontuação em português"
              >
                <SpellCheck2 className="w-3 h-3 mr-1" />
                {spellLoading ? "Corrigindo..." : "Corrigir português"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEstimateOpen(true)}
                className="h-8 text-[11px] border-violet-200 text-violet-700 hover:bg-violet-50"
                data-testid="btn-note-ai-estimate"
                title="Estimar materiais e valores com IA (métricas para orçamento)"
              >
                <Ruler className="w-3 h-3 mr-1" /> Materiais & valores (IA)
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCalcOpen(true)}
                className="h-8 text-[11px] border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="btn-note-open-calculator"
                title="Abrir calculadora rápida"
              >
                <Calculator className="w-3 h-3 mr-1" /> Calculadora
              </Button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Fotos ({photos.length})</label>
              <div className="flex gap-1.5">
                <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files || []))} data-testid="input-note-gallery" />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files || []))} data-testid="input-note-camera" />
                <Button type="button" size="sm" variant="outline" onClick={() => galleryRef.current?.click()} disabled={uploading} className="h-8 text-[11px]" data-testid="btn-note-gallery">
                  <Upload className="w-3 h-3 mr-1" /> Galeria
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => cameraRef.current?.click()} disabled={uploading} className="h-8 text-[11px]" data-testid="btn-note-camera">
                  <Camera className="w-3 h-3 mr-1" /> Câmera
                </Button>
              </div>
            </div>
            {uploading && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1 mb-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Enviando...
              </div>
            )}
            {photos.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-lg text-xs text-slate-400">
                <ImageIcon className="w-6 h-6 mx-auto mb-1 opacity-60" />
                Nenhuma foto ainda
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="aspect-square relative group rounded-lg overflow-hidden border border-slate-200">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(p)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                      aria-label="Remover foto"
                      data-testid={`btn-remove-photo-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="btn-cancel-note">Cancelar</Button>
          <Button
            onClick={save}
            disabled={saving || uploading}
            className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
            data-testid="btn-save-note"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Salvando...</> : (note?.id ? "Salvar alterações" : "Criar anotação")}
          </Button>
        </DialogFooter>
      </DialogContent>
      {/* Ajudantes IA — montados fora do fluxo do form pra não interferir */}
      <CalculatorDialog open={calcOpen} onOpenChange={setCalcOpen} />
      <MaterialEstimateDialog
        open={estimateOpen}
        onOpenChange={setEstimateOpen}
        onUseDescription={(d) => appendEstimate(d)}
        onUseObservation={(obs) => appendEstimate(obs)}
      />
    </Dialog>
  );
}
